import { useState, useCallback, useRef, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { postSendRegisterCode, postRegisterUser, ApiRequestError } from '@/api';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';

/** Matches backend `send_register_code` (6-digit numeric code). */
const CODE_LEN = 6;
const RESEND_COOLDOWN_SEC = 30;

type FieldErrors = {
  email?: string;
  sysname?: string;
  password?: string;
  confirm?: string;
  code?: string;
  terms?: string;
};

function validateFields(
  email: string,
  sysname: string,
  password: string,
  confirm: string,
  code: string,
  acceptedTerms: boolean
): FieldErrors {
  const e: FieldErrors = {};
  const em = email.trim();
  if (!em) e.email = 'Please enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = 'Please enter a valid email address.';

  const sn = sysname.trim();
  if (!sn) e.sysname = 'Please enter your display name.';
  else if (sn.length < 2) e.sysname = 'Display name must be at least 2 characters.';

  if (!password) e.password = 'Please enter a password.';
  else if (password.length < 6) e.password = 'Password must be at least 6 characters.';

  if (!confirm) e.confirm = 'Please confirm your password.';
  else if (password && confirm !== password) e.confirm = 'Passwords do not match.';

  if (code.replace(/\D/g, '').length !== CODE_LEN) {
    e.code = `Enter the ${CODE_LEN}-digit code from your email.`;
  }

  if (!acceptedTerms) {
    e.terms = 'Please read and accept the User Agreement to continue.';
  }
  return e;
}

type RegisterLocationState = { acceptedTerms?: boolean };

export default function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const shareRef = searchParams.get('ref')?.trim() || '';

  const { establishSession } = useAuth();

  const [email, setEmail] = useState('');
  const [sysname, setSysname] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [digits, setDigits] = useState<string[]>(() => Array(CODE_LEN).fill(''));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [redirectPending, setRedirectPending] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const st = location.state as RegisterLocationState | null;
    if (!st?.acceptedTerms) return;
    setAcceptedTerms(true);
    setFieldErrors((prev) => ({ ...prev, terms: undefined }));
    navigate({ pathname: '/register', search: searchParams.toString() }, { replace: true, state: null });
  }, [location.state, navigate, searchParams]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const code = digits.join('');

  const setDigitAt = useCallback((index: number, char: string) => {
    const d = char.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = d;
      return next;
    });
    if (fieldErrors.code) setFieldErrors((prev) => ({ ...prev, code: undefined }));
  }, [fieldErrors.code]);

  const focusAt = (i: number) => {
    inputRefs.current[i]?.focus();
    inputRefs.current[i]?.select();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
      setDigitAt(index - 1, '');
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }
    if (e.key === 'ArrowRight' && index < CODE_LEN - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const raw = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LEN);
    if (!raw) return;
    const next = Array(CODE_LEN).fill('');
    for (let i = 0; i < raw.length; i++) next[i] = raw[i];
    setDigits(next);
    if (fieldErrors.code) setFieldErrors((prev) => ({ ...prev, code: undefined }));
    const last = Math.min(raw.length, CODE_LEN) - 1;
    if (last >= 0) setTimeout(() => focusAt(last), 0);
  };

  const handleSendCode = async () => {
    setFormError('');
    setFormSuccess('');
    const em = email.trim();
    if (!em) {
      setFieldErrors((prev) => ({ ...prev, email: 'Please enter your email address.' }));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
      setFieldErrors((prev) => ({ ...prev, email: 'Please enter a valid email address.' }));
      return;
    }
    setFieldErrors((prev) => ({ ...prev, email: undefined }));
    setSendingCode(true);
    try {
      await postSendRegisterCode(em);
      setFormSuccess('Verification code sent. Check your inbox.');
      setCooldown(RESEND_COOLDOWN_SEC);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : 'Could not send the code.';
      setFormError(msg);
    } finally {
      setSendingCode(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    setRedirectPending(false);
    const fe = validateFields(email, sysname, password, confirm, code, acceptedTerms);
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) {
      requestAnimationFrame(() => {
        if (fe.email) document.getElementById('reg-email')?.focus();
        else if (fe.sysname) document.getElementById('reg-sysname')?.focus();
        else if (fe.password) document.getElementById('reg-password')?.focus();
        else if (fe.confirm) document.getElementById('reg-confirm')?.focus();
        else if (fe.code) focusAt(0);
        else if (fe.terms) document.getElementById('reg-terms-checkbox')?.focus();
      });
      return;
    }

    setSubmitting(true);
    try {
      const { token, userInfo } = await postRegisterUser({
        sysname: sysname.trim(),
        email: email.trim(),
        password,
        verification_code: code,
        share_code: shareRef || undefined,
      });
      establishSession(token, {
        email: userInfo?.email || email.trim(),
        name: userInfo?.user_name?.trim() || sysname.trim(),
        sysname: sysname.trim(),
        avatarUrl: userInfo?.avatar || undefined,
        role: userInfo?.sys_permissions || undefined,
        mobile: userInfo?.mobile || undefined,
        user_id: userInfo?.user_id,
        gender: typeof userInfo?.sex === 'number' ? userInfo.sex : undefined,
      });
      const okMsg = 'Account created. Redirecting…';
      setFormSuccess(okMsg);
      setRedirectPending(true);
      window.setTimeout(() => {
        navigate('/orders', { replace: true });
      }, 900);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : 'Registration failed.';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const emailInvalid = !!fieldErrors.email;
  const sysnameInvalid = !!fieldErrors.sysname;
  const passwordInvalid = !!fieldErrors.password;
  const confirmInvalid = !!fieldErrors.confirm;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 relative">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 flex items-center gap-1.5 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer group"
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-gray-200 group-hover:border-gray-300 transition-colors">
          <i className="ri-arrow-left-line text-sm"></i>
        </div>
        <span className="text-xs font-medium">Back to Home</span>
      </button>

      <div className="flex flex-col items-center mb-10">
        <div className="w-14 h-14 rounded-lg bg-green-600 flex items-center justify-center mb-4">
          <i className="ri-user-add-line text-white text-2xl"></i>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">finfins</h1>
        <p className="text-sm text-gray-500 mt-1">Create your workspace account</p>
      </div>

      <div className="w-full max-w-sm bg-white rounded-lg border border-gray-100 p-3">
        <form lang="en" noValidate onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label htmlFor="reg-email" className="block text-xs font-normal text-gray-600 mb-1.5">
              Email
            </label>
            <div className="flex gap-2 items-stretch">
              <div className="relative flex-1 min-w-0">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 pointer-events-none">
                  <i className="ri-mail-line text-base"></i>
                </div>
                <input
                  id="reg-email"
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(ev) => {
                    setEmail(ev.target.value);
                    setFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  placeholder="you@example.com"
                  aria-invalid={emailInvalid}
                  aria-describedby={emailInvalid ? 'reg-email-error' : undefined}
                  className={`w-full pl-9 pr-3 py-2.5 text-sm font-normal border rounded-lg focus:outline-none focus:ring-1 transition placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                    emailInvalid
                      ? 'border-red-300 bg-red-50/35 focus:border-red-400 focus:ring-red-100'
                      : 'border-gray-200 bg-gray-50 focus:border-green-500 focus:ring-green-200'
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={() => void handleSendCode()}
                disabled={sendingCode || cooldown > 0}
                className="shrink-0 self-stretch text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-3 transition disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap min-w-[5.5rem]"
              >
                {sendingCode ? 'Sending…' : cooldown > 0 ? `Resend (${cooldown}s)` : 'Send code'}
              </button>
            </div>
            <InlineFieldError id="reg-email-error" message={fieldErrors.email} />
          </div>

          <div>
            <div className="block text-xs font-normal text-gray-600 mb-1.5">Email verification code</div>
            <div className="flex gap-2 justify-center" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={d}
                  onChange={(ev) => {
                    const v = ev.target.value;
                    setDigitAt(i, v);
                    if (v.replace(/\D/g, '')) focusAt(Math.min(i + 1, CODE_LEN - 1));
                  }}
                  onKeyDown={(ev) => handleKeyDown(i, ev)}
                  onFocus={(ev) => ev.target.select()}
                  aria-label={`Verification code digit ${i + 1} of ${CODE_LEN}`}
                  className={`w-10 h-9 text-center text-base font-bold rounded-lg border outline-none transition-colors text-gray-900 ${
                    fieldErrors.code ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-gray-50 focus:border-green-500'
                  }`}
                />
              ))}
            </div>
            <InlineFieldError id="reg-code-error" message={fieldErrors.code} />
          </div>

          <div>
            <label htmlFor="reg-sysname" className="block text-xs font-normal text-gray-600 mb-1.5">
              Display name
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-user-smile-line text-base"></i>
              </div>
              <input
                id="reg-sysname"
                type="text"
                name="name"
                autoComplete="name"
                value={sysname}
                onChange={(ev) => {
                  setSysname(ev.target.value);
                  setFieldErrors((prev) => ({ ...prev, sysname: undefined }));
                }}
                placeholder="How we should address you"
                aria-invalid={sysnameInvalid}
                aria-describedby={sysnameInvalid ? 'reg-sysname-error' : undefined}
                className={`w-full pl-9 pr-4 py-2.5 text-sm font-normal border rounded-lg focus:outline-none focus:ring-1 transition placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                  sysnameInvalid
                    ? 'border-red-300 bg-red-50/35 focus:border-red-400 focus:ring-red-100'
                    : 'border-gray-200 bg-gray-50 focus:border-green-500 focus:ring-green-200'
                }`}
              />
            </div>
            <InlineFieldError id="reg-sysname-error" message={fieldErrors.sysname} />
          </div>

          <div>
            <label htmlFor="reg-password" className="block text-xs font-normal text-gray-600 mb-1.5">
              Password
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-lock-line text-base"></i>
              </div>
              <input
                id="reg-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                autoComplete="new-password"
                value={password}
                onChange={(ev) => {
                  setPassword(ev.target.value);
                  setFieldErrors((prev) => ({ ...prev, password: undefined }));
                }}
                placeholder="••••••••"
                aria-invalid={passwordInvalid}
                aria-describedby={passwordInvalid ? 'reg-password-error' : undefined}
                className={`w-full pl-9 pr-10 py-2.5 text-sm font-normal border rounded-lg focus:outline-none focus:ring-1 transition placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                  passwordInvalid
                    ? 'border-red-300 bg-red-50/35 focus:border-red-400 focus:ring-red-100'
                    : 'border-gray-200 bg-gray-50 focus:border-green-500 focus:ring-green-200'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                <i className={showPassword ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
              </button>
            </div>
            <InlineFieldError id="reg-password-error" message={fieldErrors.password} />
          </div>

          <div>
            <label htmlFor="reg-confirm" className="block text-xs font-normal text-gray-600 mb-1.5">
              Confirm password
            </label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                <i className="ri-lock-2-line text-base"></i>
              </div>
              <input
                id="reg-confirm"
                type={showConfirm ? 'text' : 'password'}
                name="confirm-password"
                autoComplete="new-password"
                value={confirm}
                onChange={(ev) => {
                  setConfirm(ev.target.value);
                  setFieldErrors((prev) => ({ ...prev, confirm: undefined }));
                }}
                placeholder="••••••••"
                aria-invalid={confirmInvalid}
                aria-describedby={confirmInvalid ? 'reg-confirm-error' : undefined}
                className={`w-full pl-9 pr-10 py-2.5 text-sm font-normal border rounded-lg focus:outline-none focus:ring-1 transition placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                  confirmInvalid
                    ? 'border-red-300 bg-red-50/35 focus:border-red-400 focus:ring-red-100'
                    : 'border-gray-200 bg-gray-50 focus:border-green-500 focus:ring-green-200'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 cursor-pointer"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
              >
                <i className={showConfirm ? 'ri-eye-off-line text-base' : 'ri-eye-line text-base'}></i>
              </button>
            </div>
            <InlineFieldError id="reg-confirm-error" message={fieldErrors.confirm} />
          </div>

          <div>
            <div className="flex items-start gap-2.5">
              <button
                type="button"
                id="reg-terms-checkbox"
                role="checkbox"
                aria-checked={acceptedTerms}
                aria-invalid={!!fieldErrors.terms}
                aria-describedby={fieldErrors.terms ? 'reg-terms-error' : undefined}
                onClick={() => {
                  setAcceptedTerms((v) => !v);
                  setFieldErrors((prev) => ({ ...prev, terms: undefined }));
                }}
                className={`mt-0.5 w-4 h-4 rounded flex items-center justify-center border transition cursor-pointer flex-shrink-0 ${
                  acceptedTerms ? 'bg-green-600 border-green-600' : 'bg-white border-gray-300'
                }`}
              >
                {acceptedTerms && <i className="ri-check-line text-white" style={{ fontSize: '10px' }} />}
              </button>
              <span className="text-xs text-gray-600 leading-relaxed select-none">
                I have read and agree to the{' '}
                <Link
                  to={shareRef ? `/terms?ref=${encodeURIComponent(shareRef)}` : '/terms'}
                  className="text-green-700 font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-green-200 rounded"
                  onClick={(ev) => ev.stopPropagation()}
                >
                  User Agreement
                </Link>
                .
              </span>
            </div>
            <InlineFieldError id="reg-terms-error" message={fieldErrors.terms} />
          </div>

          {formSuccess ? (
            <FormSummaryMessage message={formSuccess} variant="info" />
          ) : (
            <FormSummaryMessage message={formError} />
          )}

          <button
            type="submit"
            disabled={submitting || redirectPending}
            className="w-full h-11 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-sm font-normal rounded-lg transition disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin text-base"></i>
                Creating account…
              </span>
            ) : (
              'Create account'
            )}
          </button>
        </form>

        <p className="text-center text-xs text-gray-500 mt-6">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-green-700 font-semibold hover:underline cursor-pointer"
          >
            Sign in
          </button>
        </p>
      </div>

      <p className="text-xs text-gray-400 mt-8 text-center">
        &copy; finfins
      </p>
    </div>
  );
}
